import { writeBatch, doc, serverTimestamp, deleteField } from 'firebase/firestore';
import { db } from '../firebase';
import type {
  LandingConfig,
  CustomDomainConfig,
  InstitutionalMission,
  TenantRubric,
} from '@dosfilos/domain';

export interface UpdateTenantSettingsParams {
  tenantId: string;
  name: string;
  slug: string;
  primaryColor?: string;
  logoUrl?: string;
  documentLogoUrl?: string | null;
  mission?: InstitutionalMission | null;
  landing?: Partial<LandingConfig>;
  customDomains?: CustomDomainConfig;
  /** Phase C.1 — institutional rubric catalog. Pass [] to clear. */
  rubrics?: TenantRubric[];
  /** IANA tenant zone (e.g. `America/Lima`). Mirrored to the index so students get it. */
  timezone?: string;
  /** Numeric date ordering. Mirrored to the index alongside the zone. */
  dateFormat?: 'dd-mm-yyyy' | 'mm-dd-yyyy' | 'yyyy-mm-dd';
  /** Years of inactivity after which a student loses catalog rights. 0/null =
   *  the institution does not apply the rule (default). */
  catalogRightsExpiryYears?: number | null;
  /** Clock convention. Mirrored to the index alongside the zone. */
  timeFormat?: '24h' | '12h';
  /** TMAI site abbreviation (e.g. "HSEI") for the CAP proposal packet. */
  tmaiSiteAbbreviation?: string;
  /** The seminary's doctrinal statement (TipTap JSON), shown when signing the
   *  doctrinal attestation. */
  doctrinalStatement?: Record<string, unknown>;
}

/**
 * Atomically updates the tenant identity and config in both:
 *   - `tenants/{tenantId}`        (main document, full config)
 *   - `tenantsIndex/{tenantId}`   (discovery mirror for auth)
 *
 * Using a batch write instead of a Cloud Function because:
 *   - The admin user already has Firestore write permissions on `tenants/`.
 *   - A batch is atomic (both succeed or both fail).
 *   - No cold-start latency.
 */
export async function updateTenantSettings(params: UpdateTenantSettingsParams): Promise<void> {
  const {
    tenantId,
    name,
    slug,
    primaryColor,
    logoUrl,
    documentLogoUrl,
    mission,
    landing,
    customDomains,
    rubrics,
    timezone,
    dateFormat,
    timeFormat,
    tmaiSiteAbbreviation,
    doctrinalStatement,
  } = params;

  const batch = writeBatch(db);

  // 1. Main tenant document — partial update
  const tenantRef = doc(db, 'tenants', tenantId);
  const tenantUpdate: Record<string, unknown> = {
    name,
    slug,
    updatedAt: serverTimestamp(),
  };
  if (primaryColor !== undefined) {
    tenantUpdate['config.primaryColor'] = primaryColor;
  }
  if (logoUrl !== undefined) {
    tenantUpdate['config.logoUrl'] = logoUrl;
  }
  if (documentLogoUrl !== undefined) {
    tenantUpdate['config.documentLogoUrl'] =
      documentLogoUrl === null ? deleteField() : documentLogoUrl;
  }
  if (mission !== undefined) {
    tenantUpdate['config.mission'] = mission === null ? deleteField() : mission;
  }
  if (landing !== undefined) {
    // Flatten landing into dot-notation keys so we do a partial Firestore update
    for (const [key, value] of Object.entries(landing)) {
      tenantUpdate[`config.landing.${key}`] = value;
    }
  }
  if (rubrics !== undefined) {
    tenantUpdate['config.rubrics'] = rubrics;
  }
  if (timezone !== undefined) {
    tenantUpdate['config.timezone'] = timezone;
  }
  if (dateFormat !== undefined) {
    tenantUpdate['config.dateFormat'] = dateFormat;
  }
  if (timeFormat !== undefined) {
    tenantUpdate['config.timeFormat'] = timeFormat;
  }
  if (tmaiSiteAbbreviation !== undefined) {
    tenantUpdate['config.tmaiSiteAbbreviation'] = tmaiSiteAbbreviation;
  }
  if (doctrinalStatement !== undefined) {
    tenantUpdate['config.doctrinalStatement'] = doctrinalStatement;
  }
  if (customDomains !== undefined) {
    // Persist each domain field individually so unset keys are left untouched
    if (customDomains.landing !== undefined) {
      tenantUpdate['config.customDomains.landing'] = customDomains.landing;
    }
    if (customDomains.backoffice !== undefined) {
      tenantUpdate['config.customDomains.backoffice'] = customDomains.backoffice;
    }
  }
  batch.update(tenantRef, tenantUpdate);

  // 2. Discovery mirror — kept in sync so AuthProvider finds name, slug AND primaryColor
  const indexRef = doc(db, 'tenantsIndex', tenantId);
  batch.set(
    indexRef,
    {
      name,
      slug,
      ...(primaryColor !== undefined ? { primaryColor } : {}),
      ...(logoUrl !== undefined ? { logoUrl } : {}),
      // Mirror custom domains for Cloudflare Worker KV resolution
      ...(customDomains?.landing !== undefined ? { landingDomain: customDomains.landing } : {}),
      ...(customDomains?.backoffice !== undefined
        ? { backofficeDomain: customDomains.backoffice }
        : {}),
      // Students resolve the tenant via this index and can't read the tenant doc,
      // so the zone + format must live here for their date display on the platform host.
      ...(timezone !== undefined ? { timezone } : {}),
      ...(dateFormat !== undefined ? { dateFormat } : {}),
      ...(timeFormat !== undefined ? { timeFormat } : {}),
      updatedAt: serverTimestamp(),
    },
    { merge: true },
  );

  await batch.commit();
}
